import React, { useEffect, useState } from 'react';
import './WorkshopPage.scss';
import TitlePage from '../../components/TitlePage/TitlePage';
import { Helmet } from 'react-helmet-async';
import WorkshopCard from '../../components/WorkshopCard/WorkshopCard';
import { baseStrapiURL, baseURL } from '../../services/config';
import { useLocation } from 'react-router-dom';
import { useCollectionQuery } from '../../services/strapi/hooks';
import Loading from '../../components/Loading/Loading';
import { useQueryClient } from '@tanstack/react-query';
import Error404Page from '../Error404Page/Error404Page';

export default function WorkshopPage() {
    const location = useLocation();
    const slug = location.pathname.slice(1);
    const [isProcessing, setIsProcessing] = useState(false);

    const strapiPage = 'workshops';

    const queryClient = useQueryClient();

    // Utilisation du hook personnalisé pour récupérer les données
    const { data: strapiData, isLoading, error } = useCollectionQuery(
        strapiPage,
        slug,
        false
    );

    // Obtenir les données mises en cache ou les données actuelles
    const displayData = strapiData || queryClient.getQueryData(['strapi-collection', strapiPage, slug]);

    // État transitoire pour éviter le clignotement
    useEffect(() => {
        if (isLoading) {
            setIsProcessing(true);
        } else {
            // Délai court pour assurer une transition fluide
            const timer = setTimeout(() => setIsProcessing(false), 200);
            return () => clearTimeout(timer);
        }
    }, [isLoading]);

    // N'afficher le chargement que si les données ne sont pas disponibles et qu'on est en cours de chargement
    const showLoading = (isLoading || isProcessing) && !displayData;

    useEffect(() => {
        window.scroll(0, 0);
    }, []);

    if (showLoading) return <Loading />;
    if (!displayData || (Array.isArray(displayData) && displayData.length === 0) || error) return <Error404Page />;

    return (
        <div className="workshop-page">
            <Helmet>
                {/* Open Graph (utilisé par Facebook, LinkedIn, etc.) */}
                <meta property="og:title" content={displayData[0].SEO.Titre} />
                <meta property="og:description" content={displayData[0].SEO.Description} />
                <meta property="og:image" content={baseStrapiURL + displayData[0].SEO.PhotoReseauxSociaux_1200x630.url} />
                <meta property="og:url" content={displayData[0].Slug === null ? baseURL : baseURL + '/' + displayData[0].Slug} />
                <meta property="og:type" content="website" />

                {/* Twitter Cards */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={displayData[0].SEO.Titre} />
                <meta name="twitter:description" content={displayData[0].SEO.Description} />
                <meta name="twitter:image" content={baseStrapiURL + displayData[0].SEO.PhotoReseauxSociaux_1200x630.url} />
                <meta name="twitter:url" content={displayData[0].Slug === null ? baseURL : baseURL + '/' + displayData[0].Slug} />

                <title>{displayData[0].SEO.Titre}</title>
                <meta name="description" content={displayData[0].SEO.Description} />
                <link
                    rel="preload"
                    as="image"
                    href="/assets/workshop/background.jpg"
                    fetchPriority="high"
                />
                <link
                    rel="preload"
                    as="image"
                    href="/assets/workshop/background.webp"
                    fetchPriority="high"
                />
            </Helmet>
            <TitlePage
                picture={displayData[0].TitrePage_section.Photo}
                title1={displayData[0].TitrePage_section.Titre1}
                font1={displayData[0].TitrePage_section.Typographie1 === 'Great Vibes' ? "handwrite-title" : "classic-title"}
                title2={displayData[0].TitrePage_section.Titre2}
                font2={displayData[0].TitrePage_section.Typographie2 === 'Great Vibes' ? "handwrite-title" : "classic-title"}
            />
            <div className="content">
                <ul>
                    {
                        displayData[0].Cartes_Atelier
                            .sort((a, b) => a.ordre - b.ordre)
                            .map((workshop) => (
                                <WorkshopCard
                                    key={workshop.id} 
                                    workshop={workshop}
                                />

                            ))
                    }
                </ul>
            </div>
        </div>
    );
}
